<template>
  <div class="report-content">
    <div v-if="isLoading" class="d-flex justify-content-center align-items-center py-4 h-100">
      <v-progress-circular color="primary" indeterminate></v-progress-circular>
    </div>

    <div v-else-if="!allReportFields.length" class="w-100 h-100 d-flex justify-content-center align-items-center">
      <i class="ma-0">Please thick check column data or select report template on the left side to show the exported
        column here.</i>
    </div>

    <div class="" v-else>
      <div class="filter-container position-absolute w-100">
        <div class="d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center" v-if="selectedExportReport.name !== 'Quick'">
            <div class="filter">
              <v-menu v-model="isMenuOpen" min-width="300px" bottom :close-on-content-click="false" nudge-bottom="24px">
                <template v-slot:activator="{ on, attrs }">
                  <div class="d-flex justify-content-between align-items-center" role="button" v-bind="attrs" v-on="on">
                    <span>Filter <span class="badge badge-primary ml-2" style="line-height: inherit;">{{
                      totalAppliedFilters }}</span></span>
                    <v-icon color="#3F3F3F" small>mdi-chevron-down</v-icon>
                  </div>
                </template>
                <div class="bg-white p-3" style="height: 542px; overflow: auto; width: 400px; margin-bottom: 46px;">
                  <!-- Custom Filter -->
                  <ColumnFilter @close-menu="isMenuOpen = false" />
                </div>
              </v-menu>
            </div>
            <h5 class="ma-0 ml-3">Total <b>{{ formattedDataCount }}</b> data based on the applied filters.</h5>
          </div>
          <div :class="{ 'ml-auto': selectedExportReport.name === 'Quick' }" v-if="exportSelect !== 'collection'">
            <ColumnPagination />
          </div>
        </div>
        <div v-if="isShowLegend" class="legend my-4 d-flex" style="gap: 32px; color: #3F3F3F;">
          <v-tooltip bottom open-on-hover color="white" content-class="bg-white custom-tooltip">
            <template v-slot:activator="{ on }">
              <div v-on="on" class="actual-data d-flex align-items-center">
                <div style="width: 24px; height: 24px; background-color: #EDF4FE; border: 1px solid #D7D7D7"></div>
                <b class="ml-2">Actual Data Column</b>
              </div>
            </template>
            <span class="d-flex flex-column" style="background-color: #FFFFFF !important;">
              <b>Actual Data Column</b>
              <span style="font-size: 12px;">This is your organization's real data and will be included in the
                export</span>
            </span>
          </v-tooltip>
          <v-tooltip bottom open-on-hover color="white" content-class="bg-white custom-tooltip">
            <template v-slot:activator="{ on }">
              <div v-on="on" class="sample-data d-flex align-items-center">
                <div style="width: 24px; height: 24px; background-color: #D7D7D7; border: 1px solid #D7D7D7"></div>
                <b class="ml-2">Sample Data Column</b>
              </div>
            </template>
            <span class="d-flex flex-column" style="background-color: #FFFFFF !important;">
              <b>Sample Data Column</b>
              <span style="font-size: 12px;">This is sample data for preview only. The exported report will show real
                data</span>
            </span>
          </v-tooltip>
        </div>
      </div>
      <div class="nav-container">
        <button v-if="isShowNav" class="btn-vertical prev"
          :class="{ 'btn-vertical-primary': fieldPagination.current_page > 1 }"
          @click="goToPage(fieldPagination.current_page - 1)">
          <v-icon :color="fieldPagination.current_page > 1 ? '#FFF' : '#D7D7D7'">mdi-chevron-left</v-icon>
        </button>
        <button v-if="isShowNav" class="btn-vertical next"
          :class="{ 'btn-vertical-primary': fieldPagination.current_page < fieldPagination.total_pages }"
          @click="goToPage(fieldPagination.current_page + 1)">
          <v-icon
            :color="fieldPagination.current_page < fieldPagination.total_pages ? '#FFF' : '#D7D7D7'">mdi-chevron-right</v-icon>
        </button>
      </div>
      <div class="column-container" :style="{ marginTop: isShowLegend ? '108px' : '74px' }">
        <div class="position-relative" style="width: fit-content;">

          <div class="d-flex">
            <div v-for="(column, index) in processedColumns" :key="index" class="column-item">
              <div class="column-card">
                <div class="column-header" :class="{ 'data-dummy': column.isDummy || column.isCollection }">
                  <v-tooltip v-if="!column.isDummy && !column.isCollection" bottom content-class="tooltip">
                    <template v-slot:activator="{ on, attrs }">
                      <h4 v-on="on" class="ma-0"
                        style="font-size: 16px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                        <b>{{ getColumnLabel(column) }}</b>
                      </h4>
                    </template>
                    <span style="background-color: white;">
                      <p class="ma-0">{{ getColumnLabel(column) }}</p>
                      <i style="color: #848484; font-size: 12px;">
                        <b>Original Column Name :</b>
                        {{ column.original_header_name }}
                      </i>
                    </span>
                  </v-tooltip>
                  <div v-else class="d-flex">
                    <v-icon small color="#3F3F3F" class="mr-2"
                      style="padding: 2.5px 0;">mdi-file-document-edit-outline</v-icon>
                    <h4 class="ma-0"
                      style="font-size: 16px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                      <b>{{ getColumnLabel(column) }}</b>
                    </h4>
                  </div>
                </div>
                <div class="content-container"
                  :style="{ backgroundColor: column.isDummy || column.isCollection ? '#F4F4F4' : '#FFFFFF' }">
                  <div v-if="column.isQuestionnaire">
                    <template v-if="column.previewData.length">
                      <div class="content-preview" v-for="(item, index) in column.previewData" :key="index">
                        <h4 class="ma-0" style="font-size: 16px;">
                          <span v-if="item || item === 0">{{ item }}</span>
                          <i v-else class="text-muted" style="color: #DDDDDD !important;">Empty</i>
                        </h4>
                      </div>
                    </template>
                    <div v-else>
                      <i class="text-muted text-center d-block" style="color: #BDBDBD !important;">Preview unavailable.
                        Ensure
                        your organization has data to display.</i>
                    </div>
                  </div>
                  <div class="d-flex justify-content-center align-items-center" style="height: 800px; color: #007BFF;"
                    v-else-if="!showPreview[column.id]">
                    <button @click="togglePreview(column)" class="d-flex align-items-center">
                      <v-icon small color="#007BFF" class="mr-2" style="height: fit-content;">mdi-eye-outline</v-icon>
                      Show Preview Data
                    </button>
                  </div>
                  <div v-if="showPreview[column.id]">
                    <template v-if="previewData[`${column.id}`] && previewData[`${column.id}`].length > 0">
                      <div class="content-preview" v-for="(item, index) in previewData[column.id]" :key="index">
                        <h4 class="ma-0" style="font-size: 16px;">
                          <span v-if="item || item === 0">
                            <template v-if="column.column_name === 'prevailing_wage_cents'">
                              {{ formatCurrency(item) }}
                            </template>
                            <template v-else>
                              {{ item }}
                            </template>
                          </span>
                          <i v-else class="text-muted" style="color: #DDDDDD !important;">Empty</i>
                        </h4>
                      </div>
                    </template>
                    <div v-else>
                      <i class="text-muted text-center d-block" style="color: #BDBDBD !important;">Preview unavailable.
                        Ensure
                        your organization has data to display.</i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';

import ColumnPagination from './report_detail/ColumnPagination.vue'
import ColumnFilter from './report_detail/ColumnFilter';

export default {
  components: {
    ColumnPagination,
    ColumnFilter,
  },
  computed: {
    ...mapGetters({
      allReportFields: 'reportList/allReportFields',
      fieldPagination: 'reportList/fieldPagination',
      selectedExportReport: 'reportList/selectedExportReport',
      previewDataIds: 'reportList/previewDataIds',
      isLoading: 'reportList/isLoading',
      exportSelect: "reportList/exportSelect",
      appliedFilters: "reportList/appliedFilters",
      dataCount: "reportList/dataCount",
    }),
    processedColumns() {
      return this.sortedColumns.flatMap(column => {
        if (column.data && Array.isArray(column.data)) {
          return column.data.map(item => ({
            ...item,
            parentGroup: column.group,
            position: column.position,
            isDummy: true,
            isQuestionnaire: true,
            previewData: item.previewData || []
          }));
        }
        return {
          ...column,
          isDummy: false,
          isQuestionnaire: false,
        };
      }).sort((a, b) => a.position - b.position);
    },
    isShowLegend() {
      return this.selectedExportReport?.type === 'Report::Ticket' || this.exportSelect === 'collection';
    },
    formattedDataCount() {
      if (this.dataCount == null) return '-';
      return this.dataCount.toLocaleString('en-US');
    },
    totalAppliedFilters() {
      const filters = this.selectedExportReport?.filter || {};
      return Object.values(filters).reduce((count, value) => {
        if (Array.isArray(value)) {
          const filtered = value.filter(v => v.toLowerCase?.() !== 'all');
          return count + (filtered.length > 0 ? 1 : 0);
        } else if (typeof value === 'string') {
          const val = value.trim().toLowerCase();
          return count + (val !== '' && val !== 'all' ? 1 : 0);
        } else if (value !== null && value !== undefined) {
          return count + 1;
        }
        return count;
      }, 0);
    },
    isShowNav() {
      return this.exportSelect !== 'collection';
    },
  },
  data() {
    return {
      showModal: false,
      data: null,
      showPreview: {},
      previewData: {},
      sortedColumns: [],
      isMenuOpen: false,
    }
  },
  created() {
    this.$store.dispatch('reportList/getDataCount', { id: this.$route.params.id });
  },
  methods: {
    ...mapActions({
      getAllReportFields: 'reportList/getAllReportFields',
    }),
    ...mapMutations({
      setPagination: 'reportList/setFieldPagination',
    }),
    openModal(data) {
      this.showModal = true
      this.data = { ...data }
    },
    toggleModal(payload) {
      this.showModal = payload
    },
    toggleDrawer() {
      this.$emit('setDrawerShow', true);
    },
    goToPage(page) {
      if (page < 1 || page > this.fieldPagination.total_pages) return;

      this.setPagination({
        current_page: page,
        per_page: this.fieldPagination.per_page,
      });

      this.getAllReportFields(this.$route.params.id);
    },
    async togglePreview(column) {
      const templateId = this.selectedExportReport?.id;
      const fieldId = column.id || null;
      const group = column.group || '';

      try {
        const previewData = await this.$store.dispatch('reportList/getColumnPreview', {
          templateId,
          fieldId,
          column,
          group,
          previewDataIds: this.previewDataIds
        });

        if (column.isCollection && previewData) {
          previewData.forEach(item => {
            const matchedColumn = this.allReportFields.find(col => col.header_name === item.header);

            if (matchedColumn) {
              this.$set(this.previewData, matchedColumn.id, item.data);
              this.$set(this.showPreview, matchedColumn.id, true);
            }
          });

          return;
        }

        if (previewData) {
          this.$set(this.previewData, fieldId, previewData);
          this.$set(this.showPreview, fieldId, true);
        }
      } catch (error) {
        console.error("Error in togglePreview:", error);
      }
    },
    async fetchPreviewData(column, filter = {}) {
      try {
        const previewData = await this.$store.dispatch('reportList/getColumnPreview', {
          fieldId: column.id,
          previewDataIds: this.previewDataIds || [],
          filter,
        });

        return previewData.map(item => ({
          groupId: `questionnaire_${column.columnName}`,
          columnName: item.header.toLowerCase().replace(/\s+/g, '_'),
          header_name: item.header,
          original_header_name: item.header,
          previewData: item.data || [],
        }));
      } catch (error) {
        console.error("Failed to fetch questionnaire template preview:", error);
        return [];
      }
    },
    async reFetchPreviewData() {
      if (!this.allReportFields) return;

      this.sortedColumns = await Promise.all(
        this.allReportFields.map(async (column) => {
          if (column.category_key === 'Questionnaire::Template' && column.id) {
            column.data = await this.fetchPreviewData(column, this.appliedFilters);
          }
          return column;
        })
      );
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value / 100);
    },
    getColumnLabel(column) {
      const name = column.header_name || column.original_header_name;
      if (this.selectedExportReport.type !== 'Report::Client' && ['Business Unit', 'Department'].includes(name)) {
        return `${name} (Client)`;
      }
      return name;
    },
  },
  watch: {
    allReportFields: {
      immediate: true,
      deep: true,
      async handler(newColumns) {
        this.sortedColumns = await Promise.all(
          newColumns.map(async (column) => {
            if (column.category_key === 'Questionnaire::Template' && column.id) {
              if (!column.data || column.data.length === 0) {
                column.data = await this.fetchPreviewData(column, this.appliedFilters);
              }
            }
            return column;
          })
        );
        this.previewData = {};
        this.showPreview = {};
      }
    },
    previewDataIds: {
      handler(newVal) {
        if (newVal) {
          this.showPreview = {};
          this.previewData = {};

          setTimeout(() => {
            this.reFetchPreviewData();
          }, 500);
        }
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.report-content {
  width: 100%;
  overflow-x: auto;
  color: #3F3F3F;
  position: relative;

  .filter-container {
    top: 0;
    padding: 16px;
  }

  .index {
    width: fit-content;
    background: white;
    border: 1px solid #BDBDBD;
    border-radius: 4px;
    padding: 8px;
  }

  .filter {
    width: 115px;
    background: white;
    border: 1px solid #BDBDBD;
    border-radius: 4px;
    padding: 8px;
  }

  .column-container {
    overflow: auto;
    height: calc(100vh - 200px);
    padding: 0 16px;
    position: relative;
  }

  .nav-container {
    display: flex;
    justify-content: space-between;
    width: 100%;
    height: calc(100vh - 200px);
    position: absolute;
    pointer-events: none;
  }

  .btn-vertical {
    position: sticky;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    background: white;
    border: 1px solid #D7D7D7;
    border-radius: 12px 14px;
    padding: 6px 8px 8px 8px;
    cursor: pointer;
    box-shadow: 0px 3px 4px 0px #00000026;
    height: fit-content;
    pointer-events: initial;
  }

  .btn-vertical.prev {
    left: 2px;
  }

  .btn-vertical.next {
    right: 14px;
  }

  .btn-vertical-primary {
    background-color: #007BFF;
    color: white !important;
  }

  .column-card {
    min-width: 278px;
    width: 278px;
    border: 1px solid #D7D7D7;
  }

  .column-header {
    background-color: #EDF4FE;
    padding: 10px 16px;
    display: flex;
    gap: 6px;
    align-items: center;
    justify-content: space-between;
  }

  .data-dummy {
    background-color: #D7D7D7 !important;
  }

  .content-container {
    background-color: #FFFFFF;
    height: auto;
  }

  .content-preview {
    padding: 10px 16px;
    border-bottom: 1px solid #D7D7D7;
  }

  .content-preview h4 {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
}

.custom-tooltip {
  opacity: 1 !important;
}

::v-deep .v-tooltip {
  z-index: 99 !important;
}
</style>